import { AnyAction, Dispatch, ThunkAction } from '@reduxjs/toolkit';

import PostSocialMediaApi from '@/helpers/postSocialMediaApi';

import { beginApiCall, endApiCall } from '@/reducers/config';

import type { AuthInstance } from '@/types/auth-sdk';

import { SocialMediaType } from './enumsTypes';

interface SocialMediaSdkResponse {
  firstname: string;
  lastname: string;
  email: string;
  dob?: string;
  photo?: string;
  socialMediaId: string;
  type: SocialMediaType;
}

export interface SignInParams {
  email: string;
  socialMediaId: string;
  photo?: string;
  lastName: string;
  firstName: string;
}

export interface SignUpParams extends SignInParams {
  birthDate?: string;
  photo?: string;
}

interface PostSocialMediaResponse {
  location?: string;
}

export const postSocialMediaPhoto = async (
  baseUrl: string,
  photoUrl: string,
): Promise<PostSocialMediaResponse | null> => {
  try {
    const photoResponse = await PostSocialMediaApi(baseUrl, photoUrl);
    // eslint-disable-next-line no-console
    console.log('response from social media status', photoResponse);
    return photoResponse;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('error from post socialmedia api response', err);
    return null;
  }
};

export const attemptSocialLogin =
  (
    sdkResponse: SocialMediaSdkResponse,
    auth: AuthInstance | null,
  ): ThunkAction<Promise<any>, any, unknown, AnyAction> =>
  async (dispatch: Dispatch): Promise<any> => {
    let response: any = {};
    const { firstname: firstName, lastname: lastName, email, photo, socialMediaId, type } = sdkResponse;

    try {
      dispatch(beginApiCall());

      switch (type) {
        case 'facebook':
          response = await auth?.signInFacebook({ email, socialMediaId, photo, lastName, firstName });
          break;
        case 'google':
          response = await auth?.signInGoogle({ email, socialMediaId, photo, lastName, firstName });
          break;
        case 'apple':
          response = await auth?.signInApple({ socialMediaId });
          break;
        default:
          console.warn('Unsupported social media type for login');
      }

      dispatch(endApiCall());
    } catch (err) {
      response = { error: err instanceof Error ? err.message : 'Unknown error', status: 401 };
      dispatch(endApiCall());
    }

    return response;
  };

export const attemptSocialSignUp =
  (sdkResponse: SocialMediaSdkResponse, auth: AuthInstance | null, baseUrl: string) =>
  async (dispatch: any): Promise<any> => {
    let response: any = '';
    const { firstname: firstName, lastname: lastName, email, dob: birthDate, photo, socialMediaId, type } = sdkResponse;

    try {
      dispatch(beginApiCall());
      let mediaPhotoId: string | undefined;

      if (photo) {
        const photoIdResponse = await postSocialMediaPhoto(baseUrl, photo);
        // eslint-disable-next-line no-console
        console.log('photoIdResponse', photoIdResponse);

        const mediaItemUrl = (photoIdResponse?.location || '').split('mediaitems/');
        mediaPhotoId = mediaItemUrl.length > 1 ? mediaItemUrl[1] : undefined;
        // eslint-disable-next-line no-console
        console.log('media photo id', mediaPhotoId);
      }

      const payload: SignUpParams = {
        email,
        socialMediaId,
        birthDate,
        lastName,
        firstName,
      };

      if (mediaPhotoId) {
        payload.photo = mediaPhotoId;
      }

      // eslint-disable-next-line no-console
      console.log('final payload', payload);

      switch (type) {
        case 'facebook':
          response = await auth?.signUpFacebook(payload);
          // eslint-disable-next-line no-console
          console.log('response after signup', response);
          break;
        case 'google':
          response = await auth?.signUpGoogle(payload);
          break;
        case 'apple':
          response = await auth?.signUpApple(payload);
          break;
        default:
          console.warn('Unsupported social media type for sign-up');
          break;
      }

      dispatch(endApiCall());
    } catch (err) {
      response = err;
      console.error('Error during social media sign-up:', err);
      dispatch(endApiCall());
    }

    return response;
  };
